/**
 * TextStyle extensions for TipTap — FontSize + FontFamily
 * Both use the already-installed @tiptap/extension-text-style mark.
 */

import { Extension } from '@tiptap/core';
import '@tiptap/extension-text-style';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    fontSize: {
      setFontSize: (size: string) => ReturnType;
      unsetFontSize: () => ReturnType;
    };
    fontFamily: {
      setFontFamily: (family: string) => ReturnType;
      unsetFontFamily: () => ReturnType;
    };
  }
}

function createTextStyleExtension(
  name: string,
  cssProp: string,
  getter: (el: HTMLElement) => string | null,
) {
  return Extension.create({
    name,
    addOptions() { return { types: ['textStyle'] }; },
    addGlobalAttributes() {
      return [{
        types: this.options.types,
        attributes: {
          [name]: {
            default: null,
            parseHTML: (element: HTMLElement) => getter(element),
            renderHTML: (attributes: Record<string, string | null>) => {
              if (!attributes[name]) return {};
              return { style: `${cssProp}: ${attributes[name]}` };
            },
          },
        },
      }];
    },
    addCommands() {
      return {
        [`set${name.charAt(0).toUpperCase()}${name.slice(1)}`]:
          (value: string) =>
          ({ chain }: { chain: () => { setMark: (mark: string, attrs: Record<string, unknown>) => { run: () => boolean } } }) =>
            chain().setMark('textStyle', { [name]: value }).run(),
        [`unset${name.charAt(0).toUpperCase()}${name.slice(1)}`]:
          () =>
          ({ chain }: { chain: () => { setMark: (mark: string, attrs: Record<string, unknown>) => { removeEmptyTextStyle: () => { run: () => boolean } } } }) =>
            chain().setMark('textStyle', { [name]: null }).removeEmptyTextStyle().run(),
      };
    },
  });
}

export const FontSize = createTextStyleExtension(
  'fontSize',
  'font-size',
  (el) => el.style.fontSize?.replace(/['"]+/g, '') || null,
);

export const FontFamily = createTextStyleExtension(
  'fontFamily',
  'font-family',
  (el) => el.style.fontFamily?.replace(/['"]+/g, '') || null,
);
